import { Link } from 'react-router-dom';
import { MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const FAQS = [
    {
        question: 'How does the AI Assistant decide what to say?',
        answer: 'Every assistant is built around Guided Questions — a branching tree you define yourself (a question, its possible answers, and what each answer leads to). If you connect an AI provider, the assistant can also answer open-ended questions once the guided flow ends. If you don’t, or set the provider to Custom, it stays guided-questions-only and never generates free-text replies.',
    },
    {
        question: 'What is "Custom" mode in the AI chat engine setting?',
        answer: 'Custom mode means the assistant never calls an AI provider for free-text replies. Visitors are only ever guided through your configured question tree; if they type something outside of it, they’re nudged back to the available options. It’s the right choice when you want fully predictable, scripted conversations.',
    },
    {
        question: 'Can I brand the chat widget to match my own website?',
        answer: 'Yes. From an assistant’s settings, you can set a logo, a primary color, and a button text color. The embedded widget on your own site picks these up automatically — no code changes needed on your end.',
    },
    {
        question: 'How do I add the assistant to my website?',
        answer: 'Publish your assistant, then copy the single script tag shown on its Embed tab into your site’s HTML. The widget loads asynchronously and doesn’t affect your page’s load time.',
    },
    {
        question: 'What happens to a conversation once a visitor shares their contact info?',
        answer: 'AIPA automatically detects an email address or phone number shared in a conversation and creates a Lead from it, with the full conversation attached — no manual copy-paste between your chat tool and your CRM.',
    },
    {
        question: 'How is pricing intelligence calculated?',
        answer: 'Pricing recommendations are built from your own listed rates, the competitor set you configure, and the goals you set for occupancy or margin — not a generic market average. You stay in control of every input.',
    },
    {
        question: 'Can visitors reach the assistant outside of web chat?',
        answer: 'The same assistant configuration can extend to voice and WhatsApp channels, so your guided questions and knowledge base stay consistent no matter how a visitor reaches out.',
    },
];

export function HelpCenterPage() {
    usePageSeo({
        title: 'Help Center — AIPA Support & Resources',
        description:
            "Find answers, guides, and resources to get the most out of AIPA's AI Assistant, Pricing Intelligence, and CRM tools.",
        keywords: 'AIPA help center, AIPA support, AIPA documentation, AIPA FAQ',
    });

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className="mx-auto max-w-3xl px-6 py-20 text-center">
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Help Center</h1>
                <p className="mt-6 text-lg text-muted-foreground">
                    Answers to the questions that come up most often when setting up an assistant.
                </p>
            </section>

            <section className="border-t py-16">
                <div className="mx-auto max-w-3xl divide-y px-6">
                    {FAQS.map((faq) => (
                        <div key={faq.question} className="py-6">
                            <h3 className="font-medium">{faq.question}</h3>
                            <p className="mt-2 text-sm text-muted-foreground">{faq.answer}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border-t bg-muted/30 py-16">
                <div className="mx-auto max-w-2xl px-6 text-center">
                    <h2 className="text-xl font-semibold tracking-tight">Still need a hand?</h2>
                    <p className="mt-3 text-sm text-muted-foreground">Reach out to our team and we’ll walk through your setup with you.</p>
                    <Link to="/company/contact" className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
                        Contact us →
                    </Link>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
